import React, { useMemo, useState, useEffect } from "react";

import {
  TextField,
  InputAdornment,
  Box,
  Paper,
  List,
  ClickAwayListener,
  CircularProgress,
} from "@mui/material";
import { SearchOutlined } from "@mui/icons-material";
import { useCustom } from "@refinedev/core";
import { useMarketplaceStore } from "../../../store/useMarketplaceStore";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../../hooks/useTranslation";
import { OrderOptimizedListResponse } from "../../../pages/orders/utils/types";
import { ProductDetail } from "../../../pages/product/components/types";
import { getRuntimeEnv } from "../../../providers/getRuntimeEnv";
import { OrderResultItem } from "./components/OrderResultItem";
import { ProductResultItem } from "./components/ProductResultItem";

const ASIN_REGEX = /^[A-Z0-9]{10}$/i;
const isAsin = (value: string) => ASIN_REGEX.test(value);

export const OrderSearchBar: React.FC = () => {
  const { selectedCountry } = useMarketplaceStore();
  const navigate = useNavigate();
  const { translateWithoutPrefix } = useTranslation("");
  const inputRef = React.useRef<HTMLDivElement | null>(null);

  const [value, setValue] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const [maxHeight, setMaxHeight] = useState(window.innerHeight);

  const [matchedOrders, setMatchedOrders] = useState<
    OrderOptimizedListResponse[]
  >([]);
  const [openResults, setOpenResults] = useState(false);
  const [asinProduct, setAsinProduct] = useState<ProductDetail | null>(null);
  const [triggerProductSearch, setTriggerProductSearch] = useState(false);

  const runtimeEnv = useMemo(() => getRuntimeEnv(), []);
  const isDevEnv = runtimeEnv === "dev";

  const trimmedValue = value.trim();
  const productAsin = isAsin(debouncedValue)
    ? debouncedValue.toUpperCase()
    : null;

  /* =========================
     DEBOUNCE
  ========================= */
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedValue(trimmedValue);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [trimmedValue]);

  /* =========================
     QUERY
  ========================= */
  const queries = useMemo(() => {
    if (!debouncedValue) return [];

    const normalizedValue = debouncedValue.replaceAll(/\s/g, "").toUpperCase();

    if (isAsin(debouncedValue)) {
      return [
        {
          field: "predicate",
          value: JSON.stringify({
            type: "and",
            predicates: [
              { property: "asin", type: "eq", value: normalizedValue },
            ],
          }),
        },
      ];
    }

    return [
      {
        field: "predicate",
        value: JSON.stringify({
          type: "or",
          predicates: [
            { property: "searchTerm", type: "eq", value: debouncedValue },
            { property: "supplyOrderId", type: "eq", value: debouncedValue },
          ],
        }),
      },
    ];
  }, [debouncedValue]);

  /* =========================
     FETCH ORDERS
  ========================= */
  const { refetch, isFetching } = useCustom<OrderOptimizedListResponse[]>({
    url: selectedCountry?.id
      ? `store/${selectedCountry.id}/order/optimized`
      : "",
    method: "get",
    meta: { queries },
    queryOptions: {
      enabled: false,
      staleTime: 0,
      onSuccess: (response) => {
        const orders = response.data;
        if (!orders || orders.length === 0) {
          if (productAsin && selectedCountry?.id) {
            setAsinProduct(null);
            setTriggerProductSearch(true);
          } else {
            setOpenResults(false);
          }
          return;
        }

        if (orders.length === 1) {
          navigate(`/order-detail/${orders[0].id}`);
          setOpenResults(false);
          return;
        }

        setMatchedOrders(orders);
        setOpenResults(true);
      },
    },
  });

  /* =========================
     FETCH PRODUCT (ASIN FALLBACK)
  ========================= */
  const { refetch: refetchProduct, isFetching: isFetchingProduct } =
    useCustom<ProductDetail>({
      url:
        selectedCountry?.id && productAsin
          ? `store/${selectedCountry.id}/product/${productAsin}`
          : "",
      method: "get",
      queryOptions: {
        enabled: false,
        staleTime: 0,
        onSuccess: (response) => {
          const product = response.data;
          if (!product?.asin) return;
          setAsinProduct(product);
          setOpenResults(true);
        },
      },
    });

  useEffect(() => {
    if (!triggerProductSearch) return;
    setTriggerProductSearch(false);
    refetchProduct();
  }, [triggerProductSearch]);

  /* =========================
     AUTO FETCH (DEBOUNCED)
  ========================= */
  useEffect(() => {
    if (!debouncedValue) return;
    if (!selectedCountry?.id) return;

    setAsinProduct(null);
    refetch();
  }, [debouncedValue, selectedCountry?.id]);

  /* =========================
     MANUAL SEARCH (ENTER)
  ========================= */
  const handleSearch = () => {
    if (!selectedCountry?.id) return;
    if (!trimmedValue) return;

    setDebouncedValue(trimmedValue); // debounce bypass
  };

  /* =========================
     DROPDOWN HEIGHT
  ========================= */
  useEffect(() => {
    if (!inputRef.current) return;

    const rect = inputRef.current.getBoundingClientRect();
    const availableHeight = window.innerHeight - rect.bottom;

    setMaxHeight(availableHeight * 0.66);
  }, [openResults]);

  return (
    <ClickAwayListener onClickAway={() => setOpenResults(false)}>
      <Box sx={{ position: "relative", flex: 1 }} ref={inputRef}>
        <TextField
          placeholder={translateWithoutPrefix("common.searchByAny")}
          size="small"
          fullWidth
          autoComplete="off"
          value={value}
          disabled={!selectedCountry?.id || isFetching || isFetchingProduct}
          onChange={(e) => {
            setValue(e.target.value);
            setOpenResults(false);
            setAsinProduct(null);
          }}
          onKeyDown={(e) => {
            if (e.key !== "Enter") return;
            handleSearch();
          }}
          sx={{
            backgroundColor: "background.paper",
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  {isFetching || isFetchingProduct ? (
                    <CircularProgress size={18} thickness={5} />
                  ) : (
                    <SearchOutlined />
                  )}
                </InputAdornment>
              ),
            },
          }}
        />

        {openResults && (matchedOrders.length > 1 || asinProduct !== null) && (
          <Paper
            elevation={10}
            sx={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              mt: 0.5,
              zIndex: 10,
              maxHeight,
              overflowY: "auto",
            }}
          >
            <List dense>
              {asinProduct && (
                <ProductResultItem
                  key={`product-${asinProduct.asin}`}
                  product={asinProduct}
                  isDevEnv={isDevEnv}
                  onSelect={() => {
                    navigate(
                      `/inventory/product/${asinProduct.asin?.toUpperCase()}`,
                    );
                    setOpenResults(false);
                  }}
                />
              )}
              {matchedOrders.map((order) => (
                <OrderResultItem
                  key={order.id}
                  order={order}
                  isDevEnv={isDevEnv}
                  onSelect={() => {
                    navigate(`/order-detail/${order.id}`);
                    setOpenResults(false);
                  }}
                />
              ))}
            </List>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};
